/**
 * Converte un punto espresso in frazione del video (fx, fy) nella percentuale
 * corrispondente dello stage, tenendo conto del ritaglio di object-fit: cover.
 * Restituisce percentuali, pronte per transform-origin.
 */
function coverOrigin(fx, fy, videoW, videoH, stageW, stageH) {
  const videoRatio = videoW / videoH;
  const stageRatio = stageW / stageH;

  if (stageRatio > videoRatio) {
    // Stage più largo: il video riempie in larghezza e sborda sopra e sotto.
    const displayH = stageW / videoRatio;
    const overflow = (displayH - stageH) / 2;
    return { x: fx * 100, y: ((fy * displayH - overflow) / stageH) * 100 };
  }

  // Stage più stretto: il video riempie in altezza e sborda ai lati.
  const displayW = stageH * videoRatio;
  const overflow = (displayW - stageW) / 2;
  return { x: ((fx * displayW - overflow) / stageW) * 100, y: fy * 100 };
}

/**
 * La scena verso cui porta una spinta: la prima davanti a `y` nel verso di
 * `dir`. 
 */
function nextScene(positions, y, dir, tol = 16) {
  return dir > 0
    ? positions.find((p) => p > y + tol)
    : positions.filter((p) => p < y - tol).pop();
}

/**
 * Dove assestarsi quando lo scroll si ferma per conto suo
 */
function nearestScene(positions, y, tol = 16) {
  const last = positions[positions.length - 1];
  if (!(y > positions[0] && y < last)) return undefined;
  const nearest = positions.reduce((a, b) => (Math.abs(b - y) < Math.abs(a - y) ? b : a));
  return Math.abs(nearest - y) < tol ? undefined : nearest;
}

/**
 * Segue l'andamento di una passata di scroll e dice quando ne comincia una
 * nuova. Una passata di trackpad e' una curva: i |deltaY| salgono mentre il
 * dito spinge e poi calano da soli per tutta la coda d'inerzia. Finche' calano
 * siamo dentro la stessa passata, e il suo resto va ignorato. Quando il dito
 * tocca di nuovo, il sistema ammazza l'inerzia e i valori ripartono dal basso:
 * la risalita e' il segnale che la passata e' nuova, e arriva subito, senza
 * aspettare che la coda si spenga.
 *
 * La risalita conta solo dopo che la passata si e' smorzata sotto meta' del suo
 * picco: dentro uno swipe lungo il dito accelera e rallenta di continuo, e
 * senza questa condizione ogni oscillazione varrebbe un Capitolo.
 *
 * Le tacche di rotellina hanno valore costante, quindi non risalgono mai e qui
 * non producono nulla: fra una tacca e l'altra la passata finisce per silenzio.
 *
 * Una risalita sola pero' non basta, ce ne vogliono due di fila. Quando un
 * frame salta, Chrome accorpa gli eventi wheel che non ha consegnato e al posto
 * di due delta ne arriva uno che li somma: dentro la coda e' una risalita
 * identica a quella di un dito nuovo, e valeva un Capitolo in piu' a ogni
 * singhiozzo. Il picco accorpato pero' e' uno solo e subito dopo la coda
 * riprende a calare, mentre un dito vero spinge su per piu' eventi di seguito.
 */
function trackPush(stato, abs) {
  if (!Number.isFinite(abs)) return { ...stato, nuova: false };
  const sale = abs > stato.prev + 2 && stato.prev < stato.peak / 2;
  const salite = sale ? (stato.salite || 0) + 1 : 0;
  const nuova = salite >= 2;
  return { prev: abs, peak: nuova ? abs : Math.max(stato.peak, abs), salite, nuova };
}

/**
 * Il pannello sotto il puntatore scorre per conto suo? Si risale la catena
 * cercando chi ha un overflow verticale e piu' contenuto di quanto ne mostri:
 * quello e' uno scroll di qualcun altro e non va rubato.
 */
function scrollsOnItsOwn(node) {
  for (let el = node; el instanceof Element && el !== document.body; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight) return true;
  }
  return false;
}

// I tasti che scorrono la pagina, col verso che gli tocca. Home e End restano
// al browser: sono la scorciatoia per uscire dalla sezione in un colpo.
const SCROLL_KEYS = { ArrowDown: 1, PageDown: 1, ' ': 1, ArrowUp: -1, PageUp: -1 };

class AnniversaryScroll extends HTMLElement {
  // Di quanto la Chiusura anticipa la fine dei Capitoli.
  static OUTRO_LEAD_VH = 30;

  // Quanti Capitoli per lato restano montati accanto a quello in scena. 
  static HYDRATE_RADIUS = 1;

  // Quanto dura la dissolvenza incrociata fra due backdrop. Deve combaciare con
  // la transition di .anniv__backdrop in anniversary-scroll.css
  static FADE_MS = 600;

  // Quanto dura uno scatto, per vh di Binario percorso, e la sua durata minima.
  static GLIDE_MS_PER_VH = 12;
  static GLIDE_MIN_MS = 320;

  static INSIST_MS = 500;
  static INSIST_PEAK_FRACTION = 0.35;
  static INSIST_MIN_DELTA = 3;

  connectedCallback() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track = this.querySelector('[data-anniv-track]');
    this.stage = this.querySelector('[data-anniv-stage]');
    this.hero = this.querySelector('[data-anniv-hero]');
    this.outro = this.querySelector('[data-anniv-outro]');
    this.stream = this.querySelector('[data-anniv-stream]');
    // Il punch-through parte a Apertura chiusa: a schermo c'e' la scena di
    // chiusura, ed e' su quella che Zero centre X/Y indica lo zero. Senza, il
    // poster - che chiude l'Apertura quando la scena di chiusura non c'e'.
    this.heroFrame =
      this.querySelector('[data-anniv-hero-end]') || this.querySelector('[data-anniv-hero-poster]');

    this.initOffer();

    this.heroStep = parseFloat(this.dataset.heroStep) || 100;
    this.outroStep = parseFloat(this.dataset.outroStep) || 100;
    this.punchScale = parseFloat(this.dataset.punchthroughScale) || 5;
    // Tornando su un Capitolo il video riprende da dov'era o ricomincia. Il
    // segnalibro semplicemente non si scrive: con la Mappa vuota, hydrate non
    // ha niente da ripristinare e i video nuovi nascono gia' a zero.
    this.videoRestart = this.dataset.videoRevisit === 'restart';

    this.fadeDistance = parseFloat(this.dataset.fadeDistance) || 30;
    // L'unica impostazione con minimo 0: `|| 25` trasformerebbe uno 0 voluto in 25.
    const inactive = parseFloat(this.dataset.inactiveOpacity);
    this.inactiveOpacity = (Number.isFinite(inactive) ? inactive : 25) / 100;

    let cursor = this.heroStep;
    this.chapters = Array.from(this.querySelectorAll('[data-anniv-item]')).map((el, index) => {
      const step = parseFloat(el.dataset.step) || 100;
      const solo = el.classList.contains('anniv__item--passage');
      const chapter = { el, index, step, focusVh: cursor, solo };
      cursor += step;
      return chapter;
    });
    this.chaptersEndVh = cursor;
    this.activeIndex = -1;

    this.backdrops = Array.from(this.querySelectorAll('[data-anniv-backdrop]'));
    // Dove si era rimasti, per Capitolo: il video viene buttato via uscendo,
    // il segnalibro no.
    this.positions = new Map();
    // Smontaggi in attesa che la dissolvenza finisca, per Capitolo.
    this.teardownTimers = new Map();

    this.totalVh = this.chaptersEndVh + this.outroStep;
    this.heroDone = false;

    // Con reduced motion il CSS mostra già poster, cut-out e titolo in chiaro e
    // nasconde il video: niente clone, niente play, niente scroll handler.
    if (this.reduced) return;

    if (!this.querySelector('[data-anniv-hero-video]')) {
      const heroTemplate = this.querySelector(
        this.isMobile ? '[data-anniv-hero-video-mobile]' : '[data-anniv-hero-video-desktop]'
      );
      if (heroTemplate && heroTemplate.content.firstElementChild) {
        this.querySelector('[data-anniv-hero-media]').appendChild(heroTemplate.content.cloneNode(true));
      }
    }
    this.video = this.querySelector('[data-anniv-hero-video]');

    this.initHero();

    // L'altezza del Palco e' 100svh meno la barra del menu: misurarla una volta
    // qui e a ogni resize evita un reflow sincrono per frame dentro
    // renderChapters.
    this.measureStage();
    this.updateHeroOrigin();
    this.resizeObserver = new ResizeObserver(() => {
      this.measureStage();
      this.updateHeroOrigin();
      if (this.stageTop !== this.scrollTopOffset) this.watchScroll();
    });
    this.resizeObserver.observe(this.stream || this.stage);

    this.watchScroll();

    this.scenes = [0, ...this.chapters.map((chapter) => chapter.focusVh), this.chaptersEndVh];

    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-anniv-skip]')) return;
      const layout = this.scenePositions();
      if (!layout) return;
      event.preventDefault();
      this.locked = true;
      this.holdLock();
      this.glide(layout.positions[layout.positions.length - 1], 0);
    });

    this.onWheel = (event) => {
      if (!event.deltaY) return;
      const box = this.stage.getBoundingClientRect();
      const mezzo = window.innerHeight / 2;
      if (box.top > mezzo || box.bottom < mezzo) return;
      if (!this.contains(event.target) && scrollsOnItsOwn(event.target)) return;
      if (this.step(Math.sign(event.deltaY), Math.abs(event.deltaY))) event.preventDefault();
    };
    window.addEventListener('wheel', this.onWheel, { passive: false });

    this.onTouchStart = (event) => {
      this.touchY = event.touches[0].clientY;
      this.owned = undefined;
    };
    this.onTouchMove = (event) => {
      if (this.owned === false) return;
      if (this.owned === undefined) {
        // Dito verso l'alto = si scende nella pagina.
        const dy = this.touchY - event.touches[0].clientY;
        if (!dy) return;
        this.owned = this.step(Math.sign(dy));
        if (!this.owned) return;
      }
      event.preventDefault();
    };
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onTouchMove, { passive: false });

    this.onKeyDown = (event) => {
      const dir = SCROLL_KEYS[event.key];
      if (!dir || event.metaKey || event.ctrlKey || event.altKey) return;
      // Dentro un campo le frecce muovono il cursore, e lo spazio su un bottone
      // lo preme: quei tasti non sono nostri.
      if (event.target.closest?.('input, textarea, select, [contenteditable]')) return;
      if (event.key === ' ' && event.target.closest?.('button, a, summary, [role="button"]')) return;
      if (!this.pinned) return;
      // Tasto tenuto premuto: le ripetizioni che cadono dentro uno scatto si
      // buttano senza riarmare il lock, se no non si rilascia mai e si resta
      // fermi finche' non si molla. Il lock scade 90ms dopo l'ultimo movimento,
      // quindi tenendo premuto si avanza di una scena per scatto.
      if (this.locked) {
        event.preventDefault();
        return;
      }
      if (this.step(dir)) event.preventDefault();
    };
    window.addEventListener('keydown', this.onKeyDown);

    // Scrollbar trascinata e compagnia: qui non c'e' niente da annullare, il
    // movimento e' gia' avvenuto. Resta l'assestamento a cose ferme.
    //
    // `gliding` e non `locked`: il lock e' un timer di 90ms, e su iOS ogni
    // scrollTo di uno scatto emette un scrollend suo. L'Apertura e' lo scatto
    // piu' lungo (100vh, 1200ms) e col suo ease-in il primo pixel arriva dopo
    // ~88ms: quando l'evento di scroll che riarma il lock perde la corsa coi
    // 90ms, l'assestamento trovava la pagina a una manciata di pixel dalla
    // scena di partenza e ci riportava sopra. Da fuori: la prima passata non
    // apriva mai.
    this.onScrollEnd = () => {
      if (this.gliding || this.locked || !this.pinned) return;
      const layout = this.scenePositions();
      if (!layout) return;
      const target = nearestScene(layout.positions, window.scrollY);
      if (target === undefined) return;
      this.locked = true;
      this.holdLock();
      this.glide(target, (Math.abs(target - window.scrollY) / layout.span) * this.totalVh);
    };
    // Senza scrollend (Safari sotto la 18.2) resta il comportamento di prima:
    // niente assestamento, ma niente timer a tenere d'occhio ogni scroll.
    if ('onscrollend' in window) window.addEventListener('scrollend', this.onScrollEnd);
  }

  /**
   * Il codice sconto arriva in pagina come base64 e l'indirizzo del bottone si
   * compone qui al click.
   */
  initOffer() {
    const offer = this.querySelector('[data-anniv-offer]');
    if (!offer) return;

    const trigger = offer.querySelector('[data-anniv-reveal]');
    const panel = offer.querySelector('[data-anniv-revealed]');
    const value = offer.querySelector('[data-anniv-code-value]');
    if (!trigger || !panel || !value) return;

    const code = atob(offer.dataset.code);

    trigger.addEventListener('click', () => {
      value.textContent = code;
      trigger.hidden = true;
      panel.hidden = false;

      // Lo sconto si applica da solo a chi prosegue: il codice resta a schermo
      // per chi preferisce copiarselo, ma nessuno e' costretto a incollarlo.
      // L'href di partenza e' il link nudo, cosi' senza JavaScript il bottone
      // porta comunque ai prodotti.
      const shop = offer.querySelector('[data-anniv-shop]');
      if (shop && shop.dataset.redirect) {
        const root = (offer.dataset.root || '/').replace(/\/$/, '');
        shop.href = `${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(shop.dataset.redirect)}`;
      }

      panel.querySelector('[data-anniv-code]')?.focus();
    });

    // Senza clipboard API (http, browser vecchi, permesso negato) il codice
    // resta selezionabile a mano: si toglie solo il suggerimento che promette
    // una copia che non avverrebbe.
    const copy = offer.querySelector('[data-anniv-code]');
    const hint = offer.querySelector('[data-anniv-code-hint]');
    if (!copy || !hint) return;

    if (!navigator.clipboard) {
      hint.hidden = true;
      return;
    }

    copy.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        return;
      }
      hint.textContent = hint.dataset.done;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { hint.textContent = hint.dataset.idle; }, 2000);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.copyTimer);
    if (this.teardownTimers) this.teardownTimers.forEach(clearTimeout);
    clearTimeout(this.heroFallback);
    clearTimeout(this.unlockTimer);
    cancelAnimationFrame(this.glideFrame);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.stopScroll) this.stopScroll();
    if (this.onWheel) {
      window.removeEventListener('wheel', this.onWheel);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('scrollend', this.onScrollEnd);
    }
  }

  get isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Il Palco e' agganciato, cioe' lo scroll sta dentro il Binario. Sopra e
   * sotto la sezione i tasti e l'assestamento non sono nostri: la pagina ha un
   * header e un footer, e la rotellina li' fuori non arriva nemmeno a noi
   * perche' il suo handler sta sull'elemento.
   */
  get pinned() {
    const rect = this.track.getBoundingClientRect();
    return rect.top <= this.stageTop + 1 && rect.bottom >= window.innerHeight;
  }

  /**
   * Dove cade ogni scena, in pixel di scroll assoluti, sulla stessa corsa che
   * misura Motion: parte dove il Palco si aggancia, un'altezza di barra prima
   * che il Binario arrivi in cima. undefined quando il Binario non e' piu'
   * lungo della finestra e non c'e' nulla da paginare.
   */
  scenePositions() {
    const offset = this.stageTop;
    const span = this.track.offsetHeight - window.innerHeight + offset;
    if (span <= 0) return undefined;

    const top = this.track.getBoundingClientRect().top + window.scrollY - offset;
    return { span, positions: this.scenes.map((focusVh) => top + (focusVh / this.totalVh) * span) };
  }

  measureStage() {
    const band = this.stream || this.stage;
    this.vh = band.getBoundingClientRect().height / 100;
  }

  /**
   * Di quanto il Palco parte sotto la cima della finestra: l'altezza della barra
   * del menu, che il CSS ha gia' risolto in --anniv-top. Si legge da li' e non
   * dall'header, cosi' JS e CSS non possono dissentire - se il CSS non l'ha
   * ancora scontata, anche qui vale zero, e le due cose restano d'accordo.
   */
  get stageTop() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--anniv-top')) || 0;
  }

  /**
   * Il progresso deve valere 0 quando il Palco si aggancia sotto la barra, non
   * quando il Binario tocca la cima della finestra: fra i due punti c'e'
   * l'altezza della barra, e sarebbero altrettanti pixel di scroll con la scena
   * gia' ferma e immobile. L'offset non e' rivalutabile dopo, quindi al cambio
   * di --anniv-top si rifa' l'aggancio da capo.
   */
  watchScroll() {
    if (this.stopScroll) this.stopScroll();
    this.scrollTopOffset = this.stageTop;
    this.stopScroll = Motion.scroll(
      (info) => this.render(info.y.progress * this.totalVh),
      { target: this.track, offset: [`start ${this.scrollTopOffset}px`, 'end end'] }
    );
  }

  /**
   * Torna true se il gesto e' stato preso in carico, false se davanti non c'e'
   * piu' una scena: allora il gesto resta del browser, ed e' cosi' che si esce
   * dalla sezione sopra l'Apertura e sotto la Chiusura. Non si blocca mai nulla
   * senza avere dove andare - era il vecchio bug della pagina congelata.
   */
  step(dir, abs) {
    // Il picco di prima, non quello aggiornato con questo evento: altrimenti
    // ogni delta sarebbe per definizione il 100% del proprio picco.
    const picco = (this.push && this.push.peak) || 0;
    this.push = trackPush(this.push || { prev: 0, peak: 0 }, abs);

    const soglia = Math.max(
      AnniversaryScroll.INSIST_MIN_DELTA,
      picco * AnniversaryScroll.INSIST_PEAK_FRACTION
    );
    const insiste = abs >= soglia &&
      performance.now() - (this.lastGateAt || 0) > AnniversaryScroll.INSIST_MS;

    if (this.locked && !this.push.nuova && !insiste) {
      this.holdLock();
      return true;
    }

    this.lastGateAt = performance.now();

    const layout = this.scenePositions();
    if (!layout) return false;

    const target = nextScene(layout.positions, window.scrollY, dir);
    if (target === undefined) return false;

    // La passata riparte da qui: il picco di quella prima non deve pesare sul
    // riconoscimento della prossima.
    this.push = { prev: this.push.prev, peak: this.push.prev, nuova: false };
    this.locked = true;
    this.holdLock();
    this.glide(target, (Math.abs(target - window.scrollY) / layout.span) * this.totalVh);
    return true;
  }

  /**
   * Porta lo scroll sul bersaglio in un tempo proporzionale alla distanza.
   * `scrollTo({ behavior: 'smooth' })` non andava bene qui: la sua durata
   * satura intorno ai 400ms qualunque sia la distanza, quindi il passo
   * dell'Apertura - che vale quasi tre Capitoli - scattava veloce quanto loro e
   * il punch-through dentro lo zero non si faceva vedere.
   *
   * Un tween nuovo cancella quello in corso e riparte da dove si e' arrivati:
   * e' cosi' che una passata nuova puo' scavalcare uno scatto ancora a meta'.
   */
  glide(to, distanzaVh) {
    cancelAnimationFrame(this.glideFrame);
    this.gliding = false;

    const da = window.scrollY;
    if (da === to) return;

    this.gliding = true;

    const durata = Math.max(
      AnniversaryScroll.GLIDE_MIN_MS,
      distanzaVh * AnniversaryScroll.GLIDE_MS_PER_VH
    );
    const inizio = performance.now();

    const passo = (ora) => {
      const t = Math.min((ora - inizio) / durata, 1);
      // easeInOutCubic: parte e arriva piano, cosi' l'ingresso nello zero non
      // ha uno strappo ne' in partenza ne' in atterraggio.
      const e = t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
      window.scrollTo(0, da + (to - da) * e);
      if (t < 1) this.glideFrame = requestAnimationFrame(passo);
      else this.gliding = false;
    };

    this.glideFrame = requestAnimationFrame(passo);
  }

  holdLock() {
    clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(() => { this.locked = false; }, 90);
  }

  updateHeroOrigin() {
    if (!this.hero || !this.heroFrame) return;

    const mediaW = this.heroFrame.naturalWidth;
    const mediaH = this.heroFrame.naturalHeight;
    if (!mediaW || !mediaH) {
      // Il poster non è ancora decodificato: riprova appena lo è.
      this.heroFrame.addEventListener('load', () => this.updateHeroOrigin(), { once: true });
      return;
    }

    const fx = parseFloat(this.isMobile ? this.dataset.originXMobile : this.dataset.originX) / 100;
    const fy = parseFloat(this.isMobile ? this.dataset.originYMobile : this.dataset.originY) / 100;
    const rect = this.stage.getBoundingClientRect();
    const origin = coverOrigin(fx, fy, mediaW, mediaH, rect.width, rect.height);

    this.hero.style.transformOrigin = `${origin.x}% ${origin.y}%`;
  }

  render(pos) {
    this.pos = pos;

    // Lo scatto dura quanto il movimento.
    if (this.locked) this.holdLock();

    this.renderHero(pos);
    this.renderChapters(pos);
    this.renderOutro(pos);
  }

  renderHero(pos) {
    const t = Math.min(Math.max(pos / this.heroStep, 0), 1);

    // Scrollare significa "ho finito con l'intro": chiudiamo l'apertura prima
    // di scalare, così il punch-through parte con poster, cut-out e titolo a
    // posto e non applichiamo transform a un video in riproduzione.
    if (t > 0 && !this.heroDone) this.finishHero();

    // L'invito a scrollare si spegne appena l'Apertura si muove. Attributo e
    // non stile: la dissolvenza la fa il CSS, e il confronto evita di
    // riscrivere il DOM a ogni frame.
    const moving = t > 0;
    if (moving !== this.heroMoving) {
      this.heroMoving = moving;
      this.toggleAttribute('data-hero-moving', moving);
    }

    const scale = 1 + (this.punchScale - 1) * t;

    this.hero.style.transform = `scale(${scale})`;
    this.hero.style.opacity = String(1 - t * t);
    this.hero.style.visibility = t >= 1 ? 'hidden' : 'visible';
  }

  initHero() {
    if (!this.video) {
      this.finishHero();
      return;
    }

    this.video.addEventListener('ended', () => this.finishHero(), { once: true });

    // Se l'autoplay è bloccato l'evento 'ended' non arriva mai: dopo la durata
    // attesa del video si passa comunque allo stato finale.
    const fallbackMs = 4000;
    this.heroFallback = setTimeout(() => {
      if (this.video.currentTime === 0 || this.video.paused) this.finishHero();
    }, fallbackMs);

    const attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => this.finishHero());
    }
  }

  finishHero() {
    clearTimeout(this.heroFallback);
    if (this.video) this.video.pause();
    this.heroDone = true;
    this.setAttribute('data-hero-done', '');
  }

  renderChapters(pos) {
    const vh = this.vh;
    const fade = this.fadeDistance;

    // Il Capitolo più vicino va saputo prima di calcolare le opacità: se al
    // Fuoco c'è un Paragrafo, gli altri non si vedono affatto.
    let nearest = -1;
    let nearestDistance = Infinity;
    this.chapters.forEach((chapter) => {
      const distance = Math.abs(chapter.focusVh - pos);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = chapter.index;
      }
    });
    const soloAtFocus = nearest >= 0 && this.chapters[nearest].solo;

    this.chapters.forEach((chapter) => {
      const delta = chapter.focusVh - pos;
      const distance = Math.abs(delta);

      chapter.el.style.setProperty('--anniv-y', `${delta * vh}px`);

      const involvesSolo = chapter.solo || soloAtFocus;
      const effectiveFade = involvesSolo ? Math.max(chapter.step, 1) : fade;

      const falloff = Math.min(distance / effectiveFade, 1);
      const curve = this.inactiveOpacity + (1 - this.inactiveOpacity) * Math.pow(1 - falloff, 2);
      const opacity = (1 - falloff) * curve;
      chapter.el.style.setProperty('--anniv-opacity', String(opacity));
      chapter.el.toggleAttribute('inert', opacity < 0.05);
    });

    if (nearest !== this.activeIndex) {
      if (this.chapters[this.activeIndex]) this.chapters[this.activeIndex].el.removeAttribute('data-active');
      if (this.chapters[nearest]) this.chapters[nearest].el.setAttribute('data-active', '');
      this.activeIndex = nearest;
      this.onActiveChange(nearest);
    }
  }

  renderOutro(pos) {
    const start = Math.max(this.heroStep, this.chaptersEndVh - AnniversaryScroll.OUTRO_LEAD_VH);
    const visible = pos >= start;
    this.outro.toggleAttribute('data-visible', visible);
    // Il CTA è un <a href>: invisibile deve anche essere non focalizzabile.
    this.outro.toggleAttribute('inert', !visible);
  }

  onActiveChange(index) {
    this.backdrops.forEach((backdrop, i) => {
      // Idratare prima di attivare: il backdrop non deve mai diventare
      // .is-active da vuoto.
      if (Math.abs(i - index) > AnniversaryScroll.HYDRATE_RADIUS) {
        this.release(i);
      } else if (i !== index) {
        this.suspend(i);
      }
      backdrop.classList.toggle('is-active', i === index);
    });

    const video = this.hydrate(index);
    if (!video) return;

    // Un video trattenuto dalla finestra e' ancora fermo dove l'avevamo
    // lasciato: per farlo ricominciare va riportato a zero a mano. Su uno
    // appena montato e' gia' li', e riscriverlo non costa nulla.
    if (this.videoRestart) video.currentTime = 0;

    video.addEventListener('canplay', () => {
      // Nel frattempo si puo' essere gia' andati altrove.
      if (this.activeIndex !== index) return;
      this.hydrate(index + 1);
    }, { once: true });

    video.play().catch(() => { });
  }

  /** Torna il video pronto a partire, o undefined se il Capitolo non ne ha uno. */
  hydrate(index) {
    const backdrop = this.backdrops[index];
    if (!backdrop) return undefined;

    // L'immagine si monta una volta sola e resta: release() non la tocca,
    // riscaricare la foto a ogni passaggio di confine sarebbe peggio del male.
    if (!backdrop.hasAttribute('data-image-hydrated')) {
      const imageTemplate = backdrop.querySelector('[data-anniv-backdrop-image]');
      if (imageTemplate) backdrop.appendChild(imageTemplate.content.cloneNode(true));
      backdrop.setAttribute('data-image-hydrated', '');
    }

    // C'e' uno smontaggio in attesa: il video e' ancora appeso e integro, si
    // riprende quello. Senza questo, tornare indietro entro la dissolvenza
    // monterebbe un secondo video accanto al primo - e ricaricherebbe da capo
    // un filmato che e' gia' tutto li'.
    const pending = this.teardownTimers.get(index);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.teardownTimers.delete(index);
      backdrop.setAttribute('data-hydrated', '');
      return backdrop.querySelector('video');
    }

    if (backdrop.hasAttribute('data-hydrated')) return backdrop.querySelector('video');

    const template =
      (this.isMobile && backdrop.querySelector('[data-anniv-backdrop-video-mobile]')) ||
      backdrop.querySelector('[data-anniv-backdrop-video]');
    if (!template) return undefined;

    backdrop.appendChild(template.content.cloneNode(true));
    backdrop.setAttribute('data-hydrated', '');
    const video = backdrop.querySelector('video');
    if (!video) return undefined;

    // Si riprende da dov'era rimasto. Il tempo si puo' assegnare solo quando il
    // video sa quanto dura, prima di loadedmetadata l'assegnazione viene
    // ignorata in silenzio.
    const resumeAt = this.positions.get(index);
    if (resumeAt) {
      video.addEventListener('loadedmetadata', () => { video.currentTime = resumeAt; }, { once: true });
    }

    video.load();
    return video;
  }

  /**
   * Ferma un Capitolo senza smontarlo: il video resta appeso con il suo buffer
   * e il suo ultimo fotogramma, e tornarci non costa ne' rete ne' un'immagine
   * ferma al posto del filmato. Il segnalibro si prende qui perche' e' l'unico
   * momento in cui currentTime e' ancora quello giusto.
   */
  suspend(index) {
    const backdrop = this.backdrops[index];
    if (!backdrop || !backdrop.hasAttribute('data-hydrated')) return;

    const video = backdrop.querySelector('video');
    if (!video) return;

    video.pause();
    if (!this.videoRestart && video.currentTime) this.positions.set(index, video.currentTime);
    return video;
  }

  release(index) {
    const video = this.suspend(index);
    if (!video) return;

    const backdrop = this.backdrops[index];

    // Via subito il contrassegno: per tutti gli altri il Capitolo e' gia'
    // rilasciato, e un secondo release() nel frattempo non deve rifare nulla.
    backdrop.removeAttribute('data-hydrated');

    this.teardownTimers.set(index, setTimeout(() => {
      this.teardownTimers.delete(index);
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.removeAttribute('src');
      video.load();
      video.remove();
    }, AnniversaryScroll.FADE_MS));
  }
}

if (!customElements.get('anniversary-scroll')) {
  customElements.define('anniversary-scroll', AnniversaryScroll);
}
